/**
 * ProjectContext.js
 * 
 * Context provider for project data.
 * Manages the current project state and operations.
 */
import React, { createContext, useState, useEffect, useContext } from 'react';
import { StorageContext } from './StorageContext';

// Create the context
export const ProjectContext = createContext(null);

/**
 * Project context provider with initial project data
 * @param {Object} props - Component props
 * @param {Object} props.project - Initial project data
 * @param {Function} props.updateProject - External update function
 * @param {React.ReactNode} props.children - Child components
 */
export const ProjectProvider = ({ project: initialProject, updateProject: externalUpdate, children }) => {
  const [project, setProject] = useState(initialProject);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { storage } = useContext(StorageContext);

  // Update project in state and storage
  const updateProject = async (updatedProject) => {
    try {
      setLoading(true);
      setError(null);
      
      // Update in storage
      await storage.updateProject(updatedProject);
      
      // Update local state
      setProject(updatedProject);
      
      // Call external update if provided
      if (externalUpdate) {
        externalUpdate(updatedProject);
      }
      
      return true;
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to update project');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Set initial project
  useEffect(() => {
    setProject(initialProject);
  }, [initialProject]);

  // Update project phase
  const updatePhase = async (newPhase) => {
    if (!project) return false;
    
    const updatedProject = {
      ...project,
      currentPhase: newPhase,
      updated: Date.now()
    };
    
    return updateProject(updatedProject);
  };

  return (
    <ProjectContext.Provider 
      value={{ 
        project,
        updateProject,
        updatePhase,
        loading,
        error
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

/**
 * Hook for dynamically loading a project by ID
 * @param {string} projectId - Project ID to load
 * @returns {Object} Project context values with loading state
 */
export const useProjectLoader = (projectId) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { storage } = useContext(StorageContext);

  // Load project by ID
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const projectData = await storage.getProject(projectId);
        
        if (!projectData) {
          setError('Project not found');
          return;
        }
        
        setProject(projectData);
      } catch (err) {
        console.error('Error loading project:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    
    loadProject();
  }, [projectId, storage]);

  // Update project in state and storage
  const updateProject = async (updatedProject) => {
    try {
      setLoading(true);
      setError(null);
      
      // Update in storage
      await storage.updateProject(updatedProject);
      
      // Update local state
      setProject(updatedProject);
      
      return true;
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to update project');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update project phase
  const updatePhase = async (newPhase) => {
    if (!project) return false;
    
    const updatedProject = {
      ...project,
      currentPhase: newPhase,
      updated: Date.now()
    };
    
    return updateProject(updatedProject);
  };

  return {
    project,
    updateProject,
    updatePhase,
    loading,
    error
  };
};
